import { useState, forwardRef, useImperativeHandle } from "react";
import { mapApiTag, MappedTag } from "@/utils/tagMapping";
import { Badge } from "@/components/ui/badge";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { HardDrive, Users } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildTagActions } from "@/components/actions";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { DataTable } from "@/components/ui/data-table";
import { StatusCell, DateTimeCell, SortableHeader } from "@/components/ui/table-cells";
import { TagIcon } from "./TagIcon";
import { CustomerBadge } from "@/components/ui/customer-badge";
import { useTagResourceCounts } from "@/hooks/useTagResourceCounts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";
import * as tenantsApi from "@/services/tenantsApi";
import { setTenantCache } from "@/utils/customerLookup";
import { normalizeCriteria } from "@/utils/tagCriteriaNormalizer";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Tag = MappedTag;

export interface TagListRef { refreshTags: () => void; }
interface TagListProps { searchQuery?: string; typeFilter?: string | null; }

const ResourceCounts = ({ tagId }: { tagId: string }) => {
  const { counts, isLoading } = useTagResourceCounts(tagId);
  if (isLoading) return <span className="text-muted-foreground">Loading...</span>;
  return (
    <div className="flex items-center gap-2 text-sm">
      <div className="flex items-center gap-1"><HardDrive className="h-3 w-3 text-muted-foreground" /><span>{counts.devices}</span></div>
      <div className="flex items-center gap-1"><Users className="h-3 w-3 text-muted-foreground" /><span>{counts.total}</span></div>
    </div>
  );
};

export const TagList = forwardRef<TagListRef, TagListProps>(({ searchQuery = "", typeFilter = null }, ref) => {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [deletingTagId, setDeletingTagId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: apiTags = [], isLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: () => tagsApi.listTags(),
  });

  const { data: tenantMap = {} } = useQuery({
    queryKey: ["tenants-map"],
    queryFn: async () => {
      const dtos = await tenantsApi.listTenants();
      const map: Record<string, string> = {};
      (dtos || []).forEach((dto: any) => {
        const id = dto.tenantId || dto.tenant_id || dto.id || "";
        const name = dto.tenantName || dto.tenant_name || dto.name || id;
        if (id) map[id] = name;
      });
      setTenantCache(Object.entries(map).map(([id, name]) => ({ id, name })));
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  const resolveCustomerNames = (ids: string[]) => ids.map(id => tenantMap[id] || id);

  const tags: Tag[] = apiTags.map((t: any) => mapApiTag(t));

  const deleteTag = async (id: string) => {
    try {
      await tagsApi.deleteTagById(id);
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      toast.success("Tag deleted");
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast.error("Failed to delete tag");
    }
  };

  useImperativeHandle(ref, () => ({
    refreshTags: () => queryClient.invalidateQueries({ queryKey: ["tags"] }),
  }));

  const formatCriteria = (criteria: any) => {
    const parsed = normalizeCriteria(criteria);
    if (!parsed) return <span className="text-muted-foreground">—</span>;
    return (
      <div className="flex flex-wrap items-center gap-1">
        {parsed.conditions.map((p, i) => (
          <span key={i} className="inline-flex items-center gap-1">
            {i > 0 && <Badge variant="outline" className="text-[10px] px-1 py-0 font-semibold">{parsed.logic}</Badge>}
            <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono whitespace-nowrap">{p}</code>
          </span>
        ))}
      </div>
    );
  };

  const columns: ColumnDef<Tag>[] = [
    { accessorKey: "name", header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (<div className="flex items-center space-x-2"><TagIcon iconName={row.original.icon} color={row.original.color} size={12} /><Link to={`/tags/${row.original.id}`} className="font-medium hover:text-primary underline-offset-4 hover:underline">{row.getValue("name")}</Link></div>), },
    { accessorKey: "type", header: ({ column }) => <SortableHeader column={column}>Type</SortableHeader>, cell: ({ row }) => { const type = row.getValue("type") as string; return <Badge variant="outline" className="capitalize">{type}</Badge>; }, },
    { accessorKey: "description", header: ({ column }) => <SortableHeader column={column}>Description</SortableHeader>, cell: ({ row }) => <div className="max-w-xs truncate">{row.getValue("description") || "No description"}</div>, },
    { accessorKey: "criteria", header: "Criteria", cell: ({ row }) => <div className="text-sm text-muted-foreground">{formatCriteria(row.getValue("criteria"))}</div>, },
    { accessorKey: "customers", header: "Customers", cell: ({ row }) => {
      const customers = row.getValue("customers") as string[];
      const customerNames = resolveCustomerNames(customers || []);
      return (<div className="flex flex-wrap gap-1">{customerNames.length > 0 ? customerNames.slice(0, 2).map((n, i) => <CustomerBadge key={i} customer={n} />) : <Badge variant="outline" className="text-muted-foreground text-xs">None</Badge>}{customerNames.length > 2 && <Badge variant="outline" className="text-xs">+{customerNames.length - 2} more</Badge>}</div>);
    }},
    { accessorKey: "resource_counts", header: "Resources", cell: ({ row }) => <ResourceCounts tagId={row.original.id} /> },
    { accessorKey: "created_at", header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>, cell: ({ row }) => <DateTimeCell date={row.getValue("created_at")} format="date" />, },
    { id: "actions", header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (<ActionsDropdown actions={buildTagActions({
        surface: "list",
        onView: () => navigate(`/tags/${row.original.id}`),
        onEdit: () => {},
        onDelete: () => setDeletingTagId(row.original.id),
      })} />), },
  ];

  const filteredTags = tags.filter((tag) => {
    if (typeFilter && tag.type !== typeFilter) return false;
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      tag.name.toLowerCase().includes(q) ||
      tag.description?.toLowerCase().includes(q) ||
      tag.type.toLowerCase().includes(q)
    );
  });

  if (isLoading) return <div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  const deletingTag = tags.find(tag => tag.id === deletingTagId);

  return (
    <>
      <DataTable data={filteredTags} columns={columns} enableRowSelection={true} enableColumnResizing={true} enableSorting={true} columnSizing={columnSizing} onColumnSizingChange={setColumnSizing}
        emptyMessage={tags.length === 0 ? "No tags created yet. Create your first tag to start organizing devices." : "No tags match your search. Try adjusting your search criteria."} />
      <AlertDialog open={!!deletingTagId} onOpenChange={(open) => !open && setDeletingTagId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>{deletingTag && (<>Are you sure you want to delete the tag <strong>"{deletingTag.name}"</strong>? This action cannot be undone and will remove all associations.</>)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deletingTagId) { deleteTag(deletingTagId); setDeletingTagId(null); } }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});
